import { useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedAxis } from "../../store/slices/cartesian";
import { selectConfigurationType } from "../../store/slices/common";
import {
  selectCurrentAxisId,
  selectFetchBundlesPayload,
  selectFiltersWrapperOpened,
  selectPossibleFiltersValues,
  setFetchedBundles
} from "../../store/slices/retrofit";
import { isNumber } from "../../store/utils/utils";
import { getBundles, getProductInformations } from "../../utils/common/api/api";
import { formatConnectorType, formatMotorEncoder, formatNbPhases, formatShaftEnd } from "./Devices/Filters/utils";

export const useGetRetrofitBundles = (filters, paging, orderBy) => {
  const selectedAxis = useSelector(selectSelectedAxis);
  const filtersWrapperOpened = useSelector(selectFiltersWrapperOpened);
  const possibleMotorFiltersValues = useSelector(selectPossibleFiltersValues)?.motor;
  const axisId = useSelector(selectCurrentAxisId);
  const storedApplicationDataDependencies = useSelector(
    (state) => state.retrofit.configuration?.configData?.axes?.find((a) => a.id === axisId)?.axisState?.dependencies
  );
  const fetchBundlesPayload = useSelector(selectFetchBundlesPayload);
  const configurationType = useSelector(selectConfigurationType);
  const dispatch = useDispatch();
  const lang = window.msizerConfiguration?.locale || "en-WW";

  const processBundles = async (bundles) => {
    // get the list of ids of each motor, drive, gearbox and adaptation kits
    let motorIds = [];
    let driveIds = [];
    let gearboxIds = [];
    let adaptationKitIds = [];
    bundles.forEach((singleBundle) => {
      motorIds.push(singleBundle.motor.commercialReference);
      driveIds.push(singleBundle.drive.commercialReference);
      gearboxIds.push(singleBundle.gearbox?.commercialReference);
      adaptationKitIds.push(singleBundle.adaptationKit?.commercialReference);
    });
    //delete doublons
    motorIds = Array.from(new Set(motorIds));
    driveIds = Array.from(new Set(driveIds));
    gearboxIds = Array.from(new Set(gearboxIds));
    adaptationKitIds = Array.from(new Set(adaptationKitIds));
    // call the schneider's api for translation
    const motorInfosTranslated = await getProductInformations(motorIds);
    const driveInfosTranslated = await getProductInformations(driveIds);
    const gearboxInfosTranslated = await getProductInformations(gearboxIds);
    const adaptationKitInfosTranslated = await getProductInformations(adaptationKitIds);
    // add translation informations to bdls object
    bundles.forEach((singleBundle) => {
      // console.log("singleBundle", singleBundle);
      if (singleBundle.motor) {
        singleBundle.motor.translationData = motorInfosTranslated.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.motor?.commercialReference
        );
      }
      if (singleBundle.drive) {
        singleBundle.drive.translationData = driveInfosTranslated.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.drive?.commercialReference
        );
      }
      if (singleBundle.gearbox) {
        singleBundle.gearbox.translationData = gearboxInfosTranslated?.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.gearbox?.commercialReference
        );
      }
      if (singleBundle.adaptationKit) {
        singleBundle.adaptationKit.translationData = adaptationKitInfosTranslated?.filter(
          (elt) => elt?.catalogue_product?.id === singleBundle?.adaptationKit?.commercialReference
        );
      }
    });

    return bundles;
  };

  const {
    refetch: fetchRetrofitBundles,
    isLoading,
    isFetching
  } = useQuery(
    "FETCH_BUNDLES",
    async () => {
      try {
        const payload = {
          locale: lang,
          paging,
          search: {
            applicationDataFilter: {
              supplyVoltage:
                filters?.driveFilter.supplyVoltage?.length > 0 ? filters?.driveFilter.supplyVoltage : undefined,
              nominalTorqueOfMotor: filters?.motorFilter.nominalTorque,
              nominalSpeedOfMotor:
                filters?.motorFilter.nominalSpeed?.min === possibleMotorFiltersValues.nominalSpeed.min &&
                filters?.motorFilter.nominalSpeed?.max === possibleMotorFiltersValues.nominalSpeed.max
                  ? undefined
                  : filters?.motorFilter.nominalSpeed,
              nominalTorque:
                storedApplicationDataDependencies?.nominalTorque?.min && filters?.applicationDataFilter?.nominalTorque
                  ? {
                      min: parseFloat(filters?.applicationDataFilter?.nominalTorque),
                      max: parseFloat(storedApplicationDataDependencies.nominalTorque.min * 1.3)
                    }
                  : undefined,
              // nominalSpeed is named nominalSpeed but actually it's considered as a minimum
              nominalSpeed: isNumber(filters?.applicationDataFilter?.nominalSpeed)
                ? {
                    min: Number(filters?.applicationDataFilter?.nominalSpeed)
                  }
                : undefined,
              withGearbox: filters?.motorFilter.withGearbox,
              length: isNumber(filters?.applicationDataFilter?.maxLength)
                ? { max: Number(filters?.applicationDataFilter?.maxLength) }
                : undefined,
              withBrake:
                filters?.motorFilter.holdingBrake.length !== 1 ? undefined : filters?.motorFilter.holdingBrake[0]
            },
            motorFilter: {
              type: filters?.motorFilter.motorType?.length > 0 ? filters?.motorFilter.motorType : undefined,
              nominalTorqueOfMotor: filters?.motorFilter.nominalTorque,
              encoder:
                formatMotorEncoder(filters?.motorFilter.motorEncoder)?.length > 0
                  ? formatMotorEncoder(filters?.motorFilter.motorEncoder)
                  : undefined,
              connector:
                formatConnectorType(filters?.motorFilter.connectorType)?.length > 0
                  ? formatConnectorType(filters?.motorFilter.connectorType)
                  : undefined,
              flangeSize: filters?.motorFilter.flangeSize,
              withBrake:
                filters?.motorFilter.holdingBrake.length !== 1 ? undefined : filters?.motorFilter.holdingBrake[0],
              length: filters?.motorFilter.length,
              ipProtections:
                filters?.motorFilter.ipDegreeOfProtection.length > 0
                  ? filters?.motorFilter.ipDegreeOfProtection
                  : undefined,
              shaftEnd:
                formatShaftEnd(filters?.motorFilter.shaftEnd)?.length > 0
                  ? formatShaftEnd(filters?.motorFilter.shaftEnd)
                  : undefined,
              peakStallTorque: filters?.motorFilter.peakStallTorque,
              continuousStallTorque: filters?.motorFilter.continuousStallTorque
            },
            driveFilter: {
              type: filters?.driveFilter.driveType?.length > 0 ? filters?.driveFilter.driveType : undefined,
              outputCurrents:
                filters?.driveFilter.outputCurrent.length > 0 ? filters?.driveFilter.outputCurrent : undefined,
              communicationInterfaces:
                filters?.driveFilter.driveInterface?.length > 0 ? filters?.driveFilter.driveInterface : undefined,
              nominalPower:
                filters?.driveFilter.nominalPower?.length > 0 ? filters?.driveFilter.nominalPower : undefined,
              switchingFreq:
                filters?.driveFilter.switchingFreq?.length > 0 ? filters?.driveFilter.switchingFreq : undefined,
              format: filters?.driveFilter.format?.length > 0 ? filters?.driveFilter.format : undefined,
              nbPhases:
                formatNbPhases(filters?.driveFilter.nbPhases)?.length > 0
                  ? formatNbPhases(filters?.driveFilter.nbPhases)
                  : undefined,
              outputVoltages:
                filters?.driveFilter?.driveVoltages.length > 0 ? filters?.driveFilter.driveVoltages : undefined
            },
            gearboxFilter: {
              manufacturer: undefined,
              type: filters?.gearboxFilter.gearboxType?.length > 0 ? filters?.gearboxFilter.gearboxType : undefined,
              precision: undefined,
              reductionRatio: filters?.gearboxFilter.ratio.length > 0 ? filters?.gearboxFilter.ratio : undefined,
              specification: undefined,
              efficiency: filters?.gearboxFilter.efficiency?.length > 0 ? filters?.gearboxFilter.efficiency : undefined,
              productOrComponentType:
                filters?.gearboxFilter.productOrComponentType?.length > 0
                  ? filters?.gearboxFilter.productOrComponentType
                  : undefined,
              torsionalBacklash: filters?.gearboxFilter?.torsionalBacklash,
              inputDiameter:
                filters?.gearboxFilter.inputDiameter?.length > 0 ? filters?.gearboxFilter.inputDiameter : undefined,
              externalDiameter:
                filters?.gearboxFilter.externalDiameter?.length > 0
                  ? filters?.gearboxFilter.externalDiameter
                  : undefined
            }
          },
          order: orderBy,
          configType: configurationType,
          selectedAxis: selectedAxis?.axis?.axisGCR
        };
        // console.log("fetch bundles payload", payload);

        // check if payload is different than fetchBundlesPayload
        if (JSON.stringify(payload) !== JSON.stringify(fetchBundlesPayload)) {
          console.log("fetch bundles : payload is different than the last one");
          const bdls = await getBundles(payload);
          console.log("bdls", bdls);
          if (bdls.cartesianBundles) {
            console.log("process cartesian bundles");
            const tmpBundles = await processBundles(bdls.cartesianBundles.map((b) => b.bundle));
            // TODO: check how to transorm the cartesian bundles the same as the retrofit bundles
            tmpBundles.forEach((bundle) => {
              bdls.cartesianBundles.forEach((cb) => {
                if (cb.bundle.id === bundle.id) {
                  cb.bundle = bundle;
                }
              });
            });
          }

          if (bdls.bundlesFiltered) {
            console.log("process retrofit bundles");
            bdls.bundlesFiltered = await processBundles(bdls.bundlesFiltered);
          }

          dispatch(setFetchedBundles({ ...bdls, fetchBundlesPayload: payload }));

          return bdls.bundlesFiltered;
        }
        console.log("fetch bundles : payload is the same as the last one");
        return [];
      } catch (error) {
        console.error(error.response?.status);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  // when the filters change and filters wrapper is closed and !editingAxis => fetch bundles from api
  useEffect(() => {
    // check drive filters function
    const checkDriveFilters = () => {
      return (
        filters?.driveFilter?.driveInterface?.length > 0 &&
        filters?.driveFilter?.driveInterface?.[0] !== "" &&
        filters?.driveFilter?.nbPhases?.length > 0 &&
        filters?.driveFilter?.nbPhases?.[0] !== "" &&
        filters?.driveFilter?.driveVoltages?.length > 0 &&
        filters?.driveFilter?.driveVoltages?.[0] !== ""
      );
    };

    // check motor filters function
    const checkMotorFilters = () => {
      return (
        filters?.motorFilter?.ipDegreeOfProtection?.length > 0 &&
        filters?.motorFilter?.holdingBrake?.length > 0 &&
        (!(filters?.motorFilter?.withGearbox || filters?.gearboxFilter?.enabled) ||
          filters?.gearboxFilter?.productOrComponentType?.length > 0)
      );
    };

    // check gearbox filters function
    const checkGearboxFilters = () => {
      return (
        !(filters?.motorFilter?.withGearbox || filters?.gearboxFilter?.enabled) ||
        filters?.gearboxFilter?.productOrComponentType?.length > 0
      );
    };

    // if filters?.driveFilter object is not empty then check if all mandatory filters are filled
    const driveFiltersApplicable = filters?.driveFilter !== undefined && filters?.driveFilter !== null;

    // if filters?.motorFilter object is not empty then check if all mandatory filters are filled
    const motorFiltersApplicable = filters?.motorFilter !== undefined && filters?.motorFilter !== null;

    // if filters?.gearboxFilter object is not empty then check if all mandatory filters are filled
    const gearboxFiltersApplicable = filters?.gearboxFilter !== undefined && filters?.gearboxFilter !== null;

    if (
      filters &&
      (!driveFiltersApplicable || checkDriveFilters()) &&
      (!motorFiltersApplicable || checkMotorFilters()) &&
      (!gearboxFiltersApplicable || checkGearboxFilters()) &&
      !filtersWrapperOpened
    ) {
      // console.log("!driveFiltersApplicable", !driveFiltersApplicable);
      // console.log("checkDriveFilters()", checkDriveFilters());
      // console.log("!motorFiltersApplicable", !motorFiltersApplicable);
      // console.log("checkMotorFilters()", checkMotorFilters());
      // console.log("!gearboxFiltersApplicable", !gearboxFiltersApplicable);
      // console.log("checkGearboxFilters()", checkGearboxFilters());
      // console.log("!filtersWrapperOpened", !filtersWrapperOpened);
      fetchRetrofitBundles();
    }
  }, [filters, paging, orderBy]);

  return {
    isFetching: isFetching || isLoading,
    refetch: fetchRetrofitBundles
  };
};
